import React, { useEffect, useMemo, useState } from 'react';
import { Table, Button, Form, Pagination, Modal, Alert, OverlayTrigger, Tooltip, Badge, Card } from 'react-bootstrap';
import Sidebar from '../components/Sidebar';
import { apiGet, apiPost, API_BASE_URL } from '../utils/apiConfig';
import 'bootstrap-icons/font/bootstrap-icons.css';

const documentosPorPagina = 10;

function MedicalDocumentsPage() {
  const [documentos, setDocumentos] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [sidebarAbierto, setSidebarAbierto] = useState(window.innerWidth >= 992);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [documentoSeleccionado, setDocumentoSeleccionado] = useState(null);
  const [sinPermisos, setSinPermisos] = useState(false);
  const nombres = localStorage.getItem('nombres') || 'Usuario';

  // ════════════════════════════════════════════════════════════════════
  // ESTADOS PARA FILTROS MEJORADOS
  // ════════════════════════════════════════════════════════════════════
  const [filtros, setFiltros] = useState({
    cedula: '',
    correo: '',
    funcionario: '',
    estado: '',
    fechaDesde: '',
    fechaHasta: ''
  });
  const [mostrarFiltrosAvanzados, setMostrarFiltrosAvanzados] = useState(false);

  // Estados para reenvío
  const [mostrarModalReenvio, setMostrarModalReenvio] = useState(false);
  const [documentoReenvio, setDocumentoReenvio] = useState(null);
  const [correoReenvio, setCorreoReenvio] = useState('');
  const [celularReenvio, setCelularReenvio] = useState('');
  const [metodosReenvio, setMetodosReenvio] = useState([]);
  const [enviandoReenvio, setEnviandoReenvio] = useState(false);
  const [mensajeReenvio, setMensajeReenvio] = useState('');

  // ════════════════════════════════════════════════════════════════════
  // DETECTAR CAMBIOS DE TAMAÑO DE PANTALLA
  // ════════════════════════════════════════════════════════════════════
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 992;
      setIsMobile(mobile);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ⭐ FUNCIÓN PARA DECODIFICAR NOMBRES CON TILDES ⭐
  const decodificarNombre = (nombre) => {
    if (!nombre) return '';
    try {
      return decodeURIComponent(nombre);
    } catch (error) {
      console.warn('Error decodificando nombre:', nombre);
      return nombre;
    }
  };

  const totalPaginas = useMemo(() => Math.ceil(total / documentosPorPagina) || 1, [total]);

  const estadoBadge = (estado) => {
    const map = {
      rechazado: 'danger',
      pendiente: 'warning',
      finalizado: 'success',
      firmado: 'info',
      anulado: 'secondary'
    };
    
    const iconos = {
      rechazado: '❌ ',
      pendiente: '⌛ ',
      finalizado: '✅ ',
      firmado: '✍️ ',
      anulado: '🚫 '
    };
    
    return (
      <Badge 
        bg={map[estado] || 'secondary'} 
        className={estado === 'pendiente' ? 'text-dark' : ''}
      >
        {iconos[estado] || ''}{(estado || '').toUpperCase()}
      </Badge>
    );
  };

  const copyToClipboard = (text) => navigator.clipboard?.writeText(text).catch(() => {});

  const renderMetodoEnvio = (metodos) => {
    // Manejo robusto para diferentes formatos
    let metodosArray = [];
    
    if (Array.isArray(metodos)) {
      if (metodos.length > 0 && typeof metodos[0] === 'string' && metodos[0].startsWith('[')) {
        try {
          metodosArray = JSON.parse(metodos[0]);
        } catch {
          metodosArray = metodos;
        }
      } else {
        metodosArray = metodos;
      }
    } else if (typeof metodos === 'string') {
      try {
        metodosArray = JSON.parse(metodos);
      } catch {
        metodosArray = [metodos];
      }
    }
    
    if (metodosArray.length === 0) {
      return <span className="text-muted small">N/A</span>;
    }
    
    return (
      <div className="d-flex gap-2 flex-wrap justify-content-center">
        {metodosArray.map((m, i) => {
          const map = { 
            correo: ['bi-envelope-fill', 'Correo', 'primary'], 
            sms: ['bi-chat-dots-fill', 'SMS', 'success'],
            whatsapp: ['bi-whatsapp', 'WhatsApp', 'success']
          };
          const conf = map[m];
          return conf ? (
            <OverlayTrigger key={i} placement="top" overlay={<Tooltip>{conf[1]}</Tooltip>}>
              <i className={`bi ${conf[0]} text-${conf[2]}`} style={{ fontSize: '1.2rem' }} />
            </OverlayTrigger>
          ) : null;
        })}
      </div>
    );
  };

  useEffect(() => {
    const permisos = localStorage.getItem('permisos');
    if (permisos) {
      const p = JSON.parse(permisos);
      if (!p.verDocumentosMedicos) setSinPermisos(true);
    } else {
      setSinPermisos(true);
    }
  }, []);

  const obtenerDocumentosMedicos = async () => {
    try {
      const params = { 
        page: pagina, 
        limit: documentosPorPagina, 
        tipo: 'medico',
        // Filtros
        cedula: filtros.cedula || undefined,
        correo: filtros.correo || undefined,
        funcionario: filtros.funcionario || undefined,
        estado: filtros.estado || undefined,
        fechaDesde: filtros.fechaDesde || undefined,
        fechaHasta: filtros.fechaHasta || undefined
      };
      
      // Limpiar parámetros undefined
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);
      
      const datos = await apiGet('/api/documentos-dashboard', { params });
      setDocumentos(Array.isArray(datos.documentos) ? datos.documentos : []);
      setTotal(Number(datos.total || 0));
    } catch (err) {
      console.error('Error al obtener documentos médicos:', err);
    }
  };

  // ════════════════════════════════════════════════════════════════════
  // AUTO-ACTUALIZACIÓN INTELIGENTE
  // Solo se actualiza automáticamente si NO hay filtros activos
  // ════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (sinPermisos) return;

    // Cargar documentos al montar o cambiar página/filtros
    obtenerDocumentosMedicos();

    // Verificar si hay filtros activos
    const filtrosActivos = Boolean(
      filtros.cedula.trim() ||
      filtros.correo.trim() ||
      filtros.funcionario.trim() ||
      filtros.estado.trim() ||
      filtros.fechaDesde.trim() ||
      filtros.fechaHasta.trim()
    );

    // Solo auto-actualizar si NO hay filtros activos
    if (!filtrosActivos) {
      console.log('🔄 Auto-actualización ACTIVA (sin filtros)');
      const id = setInterval(obtenerDocumentosMedicos, 15000);
      return () => clearInterval(id);
    } else {
      console.log('⏸️ Auto-actualización PAUSADA (filtros activos)');
    }
  }, [pagina, sinPermisos, filtros]);

  // Función para aplicar filtros
  const handleAplicarFiltros = (e) => {
    if (e) e.preventDefault();
    setPagina(1);
    obtenerDocumentosMedicos();
  };

  // Función para limpiar filtros
  const handleLimpiarFiltros = () => {
    setFiltros({
      cedula: '',
      correo: '',
      funcionario: '',
      estado: '',
      fechaDesde: '',
      fechaHasta: ''
    });
    setPagina(1);
    setTimeout(() => obtenerDocumentosMedicos(), 100);
  };

  // Verificar si hay filtros activos
  const hayFiltrosActivos = Object.values(filtros).some(v => v !== '');

  const handleBuscar = (e) => { e.preventDefault(); setPagina(1); obtenerDocumentosMedicos(); };
  const handleVisualizar = (rutaFinal) => { if (rutaFinal) { setDocumentoSeleccionado(`${API_BASE_URL}/${rutaFinal}`); setMostrarModal(true); } };
  const handleCerrarModal = () => { setMostrarModal(false); setDocumentoSeleccionado(null); };

  const handleAbrirReenvio = (doc) => {
    setDocumentoReenvio(doc);
    setCorreoReenvio(doc.correo || '');
    setCelularReenvio(doc.celular || '');
    setMetodosReenvio(doc.sendMethods || ['correo']);
    setMensajeReenvio('');
    setMostrarModalReenvio(true);
  };

  const handleToggleMetodo = (metodo) => {
    setMetodosReenvio(prev => {
      if (prev.includes(metodo)) {
        return prev.filter(m => m !== metodo);
      } else {
        return [...prev, metodo];
      }
    });
  };

  const handleReenviarDocumento = async () => {
    if (!correoReenvio && !celularReenvio) {
      setMensajeReenvio('Debes ingresar al menos un correo o celular');
      return;
    }

    if (correoReenvio && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correoReenvio)) {
      setMensajeReenvio('El correo electrónico no es válido');
      return;
    }

    if (celularReenvio && !/^\d{10}$/.test(celularReenvio)) {
      setMensajeReenvio('El celular debe tener 10 dígitos');
      return;
    }

    if (metodosReenvio.length === 0) {
      setMensajeReenvio('Selecciona al menos un método de envío');
      return;
    }

    setEnviandoReenvio(true);
    setMensajeReenvio('Reenviando documento...');

    try {
      await apiPost(`/api/reenviar-documento/${documentoReenvio._id}`, {
        correo: correoReenvio,
        celular: celularReenvio,
        sendMethods: metodosReenvio
      });

      setMensajeReenvio('✅ Documento reenviado exitosamente');
      setTimeout(() => {
        setMostrarModalReenvio(false);
        obtenerDocumentosMedicos();
      }, 2000);

    } catch (error) {
      console.error('Error al reenviar documento:', error);
      setMensajeReenvio(`❌ Error: ${error.response?.data?.message || 'No se pudo reenviar el documento'}`);
    } finally {
      setEnviandoReenvio(false);
    }
  };

  const renderPaginacion = () => {
    if (totalPaginas <= 1) return null;
    const items = [];
    const maxVisible = 5;

    items.push(<Pagination.Prev key="prev" disabled={pagina === 1} onClick={() => setPagina((p) => p - 1)} />);

    const start = Math.max(1, pagina - Math.floor(maxVisible / 2));
    const end = Math.min(totalPaginas, start + maxVisible - 1);
    const adjustedStart = Math.max(1, end - maxVisible + 1);

    if (adjustedStart > 1) {
      items.push(<Pagination.Item key={1} onClick={() => setPagina(1)}>1</Pagination.Item>);
      if (adjustedStart > 2) items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    }

    for (let i = adjustedStart; i <= end; i++) {
      items.push(<Pagination.Item key={i} active={i === pagina} onClick={() => setPagina(i)}>{i}</Pagination.Item>);
    }

    if (end < totalPaginas) {
      if (end < totalPaginas - 1) items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
      items.push(<Pagination.Item key={totalPaginas} onClick={() => setPagina(totalPaginas)}>{totalPaginas}</Pagination.Item>);
    }

    items.push(<Pagination.Next key="next" disabled={pagina === totalPaginas} onClick={() => setPagina((p) => p + 1)} />);

    return <Pagination className="justify-content-center mb-0" size={isMobile ? 'sm' : 'md'}>{items}</Pagination>;
  };

  // ════════════════════════════════════════════════════════════════════
  // ESTILOS DEL CONTENEDOR PRINCIPAL (RESPONSIVE)
  // ════════════════════════════════════════════════════════════════════
  const getMainContentStyle = () => {
    if (isMobile) {
      return {
        marginLeft: 0,
        width: '100%',
        padding: '70px 15px 20px 15px',
        transition: 'all 0.3s ease'
      };
    }
    
    return {
      marginLeft: sidebarAbierto ? '250px' : '70px',
      width: sidebarAbierto ? 'calc(100% - 250px)' : 'calc(100% - 70px)',
      padding: '20px 30px',
      transition: 'all 0.3s ease'
    };
  };

  const RowCard = ({ doc }) => (
    <Card className="mb-3 shadow-sm">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <h6 className="mb-1">
              {decodificarNombre(doc.nombres)}
              <small className="text-muted ms-2">CC: {doc.cedula}</small>
            </h6>
            <small className="text-muted d-block">
              {doc.correo || 'Sin correo'}
            </small>
          </div>
          {estadoBadge(doc.estado)}
        </div>
        <hr />
        <div className="mb-2">
          <strong>Documento:</strong> <span className="small">{doc.nombreDocumento}</span>
        </div>
        <div className="mb-2">
          <strong>Enviado por:</strong> {decodificarNombre(doc.enviadoPor) || 'N/A'}
        </div>
        <div className="mb-2">
          <strong>Métodos:</strong> {renderMetodoEnvio(doc.sendMethods)}
        </div>
        <div className="mb-2">
          <strong>Fecha:</strong> <small>{new Date(doc.createdAt).toLocaleDateString('es-CO')} - {new Date(doc.createdAt).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}</small>
        </div>
        <div className="d-flex gap-2 mt-3">
          {doc.estado === 'pendiente' && (
            <Button 
              variant="outline-warning" 
              size="sm" 
              onClick={() => handleAbrirReenvio(doc)}
            >
              <i className="bi bi-arrow-repeat me-1"></i> Reenviar
            </Button>
          )}
          {doc.estado === 'finalizado' && doc.rutaFinal?.trim() && (
            <>
              <Button variant="primary" size="sm" onClick={() => handleVisualizar(doc.rutaFinal)}>
                <i className="bi bi-eye-fill me-1"></i> Ver
              </Button>
              <a href={`${API_BASE_URL}/${doc.rutaFinal}`} download target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-success">
                <i className="bi bi-download me-1"></i> Descargar
              </a>
            </>
          )}
          {doc.estado === 'rechazado' && doc.rutaFinal?.trim() && (
            <Button variant="outline-danger" size="sm" onClick={() => handleVisualizar(doc.rutaFinal)}>
              <i className="bi bi-eye-fill me-1"></i> Ver Rechazado
            </Button>
          )}
        </div>
      </Card.Body>
    </Card>
  );

  if (sinPermisos) {
    return (
      <div className="d-flex" style={{ minHeight: '100vh' }}>
        <Sidebar onToggle={setSidebarAbierto} />
        <div className="flex-grow-1 d-flex align-items-center justify-content-center" style={getMainContentStyle()}>
          <Alert variant="danger" className="text-center">
            <h4>⛔ Acceso Denegado</h4>
            <p className="mb-0">No tienes permisos para ver documentos médicos.</p>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex" style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      <Sidebar onToggle={setSidebarAbierto} />

      <div className="flex-grow-1" style={getMainContentStyle()}>
        <div style={{ maxWidth: '100%' }}>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 mb-md-4">
            <div className="mb-2 mb-md-0">
              <h2 className="mb-1" style={{ fontSize: isMobile ? '1.3rem' : '1.75rem' }}>🏥 Documentos Médicos</h2>
              <p className="text-muted mb-0 small">Gestión y seguimiento de documentos médicos</p>
            </div>
            <div className="text-start text-md-end">
              <div className="fw-bold" style={{ fontSize: '1.1rem', color: '#1a1e4b' }}>
                👋 {decodificarNombre(nombres)}
              </div>
              <small className="text-muted text-capitalize">
                {(localStorage.getItem('rol') || 'usuario').replace('admin', ' Admin')}
              </small>
            </div>
          </div>

          <Card className="shadow-sm mb-3 mb-md-4">
            <Card.Body className="p-3">
              <Form onSubmit={handleAplicarFiltros}>
                {/* Fila principal de búsqueda */}
                <div className="row align-items-end g-2 mb-2">
                  <div className="col-12 col-md-4">
                    <Form.Label className="mb-1 small">
                      <i className="bi bi-person-vcard me-1"></i>
                      Cédula
                    </Form.Label>
                    <Form.Control 
                      type="text" 
                      placeholder="Buscar por cédula" 
                      value={filtros.cedula} 
                      onChange={(e) => setFiltros({...filtros, cedula: e.target.value})}
                      size="sm"
                    />
                  </div>
                  <div className="col-12 col-md-4">
                    <Form.Label className="mb-1 small">
                      <i className="bi bi-envelope me-1"></i>
                      Correo
                    </Form.Label>
                    <Form.Control 
                      type="text" 
                      placeholder="Buscar por correo" 
                      value={filtros.correo} 
                      onChange={(e) => setFiltros({...filtros, correo: e.target.value})}
                      size="sm"
                    />
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="d-flex gap-2">
                      <Button type="submit" variant="primary" className="flex-grow-1" size="sm">
                        <i className="bi bi-search me-1"></i>
                        Buscar
                      </Button>
                      <Button 
                        variant="outline-secondary" 
                        size="sm"
                        onClick={() => setMostrarFiltrosAvanzados(!mostrarFiltrosAvanzados)}
                        title="Filtros avanzados"
                      >
                        <i className={`bi bi-funnel${mostrarFiltrosAvanzados ? '-fill' : ''}`}></i>
                      </Button>
                      {hayFiltrosActivos && (
                        <Button variant="outline-danger" onClick={handleLimpiarFiltros} size="sm" title="Limpiar filtros">
                          <i className="bi bi-x-circle"></i>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Filtros avanzados (colapsables) */}
                {mostrarFiltrosAvanzados && (
                  <div className="row align-items-end g-2 mt-2 pt-2 border-top">
                    <div className="col-12 col-md-3">
                      <Form.Label className="mb-1 small">
                        <i className="bi bi-person-badge me-1"></i>
                        Funcionario
                      </Form.Label>
                      <Form.Control 
                        type="text" 
                        placeholder="Nombre del funcionario" 
                        value={filtros.funcionario} 
                        onChange={(e) => setFiltros({...filtros, funcionario: e.target.value})}
                        size="sm"
                      />
                    </div>
                    <div className="col-6 col-md-2">
                      <Form.Label className="mb-1 small">
                        <i className="bi bi-flag me-1"></i>
                        Estado
                      </Form.Label>
                      <Form.Select 
                        value={filtros.estado} 
                        onChange={(e) => setFiltros({...filtros, estado: e.target.value})}
                        size="sm"
                      >
                        <option value="">Todos</option>
                        <option value="pendiente">⌛ Pendiente</option>
                        <option value="firmado">✍️ Firmado</option>
                        <option value="finalizado">✅ Finalizado</option>
                        <option value="rechazado">❌ Rechazado</option>
                        <option value="anulado">🚫 Anulado</option>
                      </Form.Select>
                    </div>
                    <div className="col-6 col-md-2">
                      <Form.Label className="mb-1 small">
                        <i className="bi bi-calendar me-1"></i>
                        Desde
                      </Form.Label>
                      <Form.Control 
                        type="date" 
                        value={filtros.fechaDesde} 
                        onChange={(e) => setFiltros({...filtros, fechaDesde: e.target.value})}
                        size="sm"
                      />
                    </div>
                    <div className="col-6 col-md-2">
                      <Form.Label className="mb-1 small">
                        <i className="bi bi-calendar-check me-1"></i>
                        Hasta
                      </Form.Label>
                      <Form.Control 
                        type="date" 
                        value={filtros.fechaHasta} 
                        onChange={(e) => setFiltros({...filtros, fechaHasta: e.target.value})}
                        size="sm"
                      />
                    </div>
                    <div className="col-6 col-md-3">
                      <Button type="submit" variant="success" className="w-100" size="sm">
                        <i className="bi bi-funnel me-1"></i>
                        Aplicar Filtros
                      </Button>
                    </div>
                  </div>
                )}
              </Form>

              {/* Resumen de filtros activos */}
              <div className="mt-2 d-flex flex-wrap align-items-center gap-2">
                <span className="text-muted small">
                  <i className="bi bi-info-circle me-1"></i>
                  Mostrando {documentos.length} de {total} documentos
                </span>
                {hayFiltrosActivos && (
                  <div className="d-flex flex-wrap gap-1">
                    {filtros.cedula && (
                      <Badge bg="primary" className="d-flex align-items-center gap-1">
                        Cédula: {filtros.cedula}
                        <i className="bi bi-x" style={{cursor: 'pointer'}} onClick={() => setFiltros({...filtros, cedula: ''})}></i>
                      </Badge>
                    )}
                    {filtros.correo && (
                      <Badge bg="info" className="d-flex align-items-center gap-1">
                        Correo: {filtros.correo}
                        <i className="bi bi-x" style={{cursor: 'pointer'}} onClick={() => setFiltros({...filtros, correo: ''})}></i>
                      </Badge>
                    )}
                    {filtros.funcionario && (
                      <Badge bg="secondary" className="d-flex align-items-center gap-1">
                        Funcionario: {filtros.funcionario}
                        <i className="bi bi-x" style={{cursor: 'pointer'}} onClick={() => setFiltros({...filtros, funcionario: ''})}></i>
                      </Badge>
                    )}
                    {filtros.estado && (
                      <Badge bg="warning" text="dark" className="d-flex align-items-center gap-1">
                        Estado: {filtros.estado}
                        <i className="bi bi-x" style={{cursor: 'pointer'}} onClick={() => setFiltros({...filtros, estado: ''})}></i>
                      </Badge>
                    )}
                    {(filtros.fechaDesde || filtros.fechaHasta) && (
                      <Badge bg="success" className="d-flex align-items-center gap-1">
                        Fecha: {filtros.fechaDesde || '...'} - {filtros.fechaHasta || '...'}
                        <i className="bi bi-x" style={{cursor: 'pointer'}} onClick={() => setFiltros({...filtros, fechaDesde: '', fechaHasta: ''})}></i>
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>

          {/* Tabla Desktop */}
          <div className="d-none d-lg-block">
            <div className="table-responsive" style={{ maxHeight: '65vh', overflow: 'auto', width: '100%' }}>
              <Table bordered hover size="sm" className="mb-0">
                <thead className="table-primary" style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#cfe2ff' }}>
                  <tr>
                    <th style={{ whiteSpace: 'nowrap', width: '95px' }}>Fecha</th>
                    <th style={{ whiteSpace: 'nowrap', width: '100px' }}>Cédula</th>
                    <th style={{ minWidth: '180px' }}>Nombres</th>
                    <th style={{ minWidth: '200px' }}>Correo</th>
                    <th style={{ minWidth: '200px', maxWidth: '250px' }}>Documento</th>
                    <th style={{ whiteSpace: 'nowrap', width: '100px' }}>Estado</th>
                    <th style={{ minWidth: '130px' }}>Enviado por</th>
                    <th className="text-center" style={{ whiteSpace: 'nowrap', width: '75px' }}>Métodos</th>
                    <th className="text-center" style={{ whiteSpace: 'nowrap', width: '110px' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {documentos.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center py-5">
                        <i className="bi bi-clipboard-x" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                        <p className="text-muted mt-3 mb-1">No hay documentos médicos registrados</p>
                        {hayFiltrosActivos && <Button variant="outline-secondary" size="sm" onClick={handleLimpiarFiltros}>Limpiar filtros</Button>}
                      </td>
                    </tr>
                  ) : (
                    documentos.map((doc) => (
                      <tr key={doc._id} className={doc.estado === 'rechazado' ? 'table-danger' : ''}>
                        <td className="small" style={{ whiteSpace: 'nowrap' }}>
                          {new Date(doc.createdAt).toLocaleDateString('es-CO')}<br />
                          <small className="text-muted">
                            {new Date(doc.createdAt).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                          </small>
                        </td>

                        <td style={{ whiteSpace: 'nowrap' }}>
                          <span className="fw-bold">{doc.cedula}</span>
                          <Button variant="link" size="sm" className="p-0 ms-1 align-baseline" title="Copiar" onClick={() => copyToClipboard(doc.cedula)}>
                            <i className="bi bi-clipboard"></i>
                          </Button>
                        </td>

                        <td className="text-wrap small" style={{ wordBreak: 'break-word', fontSize: '0.9rem' }}>
                          {decodificarNombre(doc.nombres) || '-'}
                        </td>

                        <td className="text-wrap small" style={{ wordBreak: 'break-all', fontSize: '0.85rem' }}>
                          {doc.correo || '-'}
                        </td>

                        <td className="small" style={{ maxWidth: '250px', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                          <OverlayTrigger 
                            placement="top" 
                            overlay={<Tooltip>{doc.nombreDocumento || 'Sin nombre'}</Tooltip>}
                          >
                            <div style={{ 
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              cursor: 'help'
                            }}>
                              {doc.nombreDocumento || '-'}
                            </div>
                          </OverlayTrigger>
                        </td>

                        <td>{estadoBadge(doc.estado)}</td>

                        <td className="text-wrap small" style={{ wordBreak: 'break-word', fontSize: '0.85rem' }}>
                          {doc.enviadoPor && doc.enviadoPor !== 'No identificado' 
                            ? decodificarNombre(doc.enviadoPor) 
                            : <span className="text-muted fst-italic">Sin identificar</span>
                          }
                        </td>

                        <td className="text-center">{renderMetodoEnvio(doc.sendMethods)}</td>

                        <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                          {doc.estado === 'pendiente' && (
                            <Button 
                              variant="outline-warning" 
                              size="sm" 
                              title="Reenviar documento"
                              onClick={() => handleAbrirReenvio(doc)}
                              className="me-1"
                            >
                              <i className="bi bi-arrow-repeat"></i>
                            </Button>
                          )}
                          {doc.estado === 'rechazado' ? (
                            doc.rutaFinal?.trim()
                              ? <Button variant="outline-danger" size="sm" title="Ver documento rechazado" onClick={() => handleVisualizar(doc.rutaFinal)}><i className="bi bi-eye-fill" /></Button>
                              : <span className="text-muted small">N/A</span>
                          ) : doc.estado === 'pendiente' ? (
                            <span title="Pendiente de firma">⌛</span>
                          ) : doc.estado === 'firmado' ? (
                            <span className="text-info" title="Documento firmado">✍️</span>
                          ) : doc.estado === 'anulado' ? (
                            <span className="text-muted" title="Documento anulado">🚫</span>
                          ) : doc.estado === 'finalizado' && doc.rutaFinal?.trim() ? (
                            <div className="d-flex justify-content-center gap-1">
                              <Button variant="primary" size="sm" title="Ver documento" onClick={() => handleVisualizar(doc.rutaFinal)}>
                                <i className="bi bi-eye-fill"></i>
                              </Button>
                              <a href={`${API_BASE_URL}/${doc.rutaFinal}`} download target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-success" title="Descargar">
                                <i className="bi bi-download"></i>
                              </a>
                            </div>
                          ) : (
                            <span className="text-muted small">N/A</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </div>

          {/* Cards Móvil/Tablet */}
          <div className="d-block d-lg-none">
            {documentos.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-clipboard-x" style={{ fontSize: '3rem', color: '#6c757d' }} />
                <p className="text-muted mt-3">No hay documentos médicos registrados</p>
                {hayFiltrosActivos && <Button variant="outline-secondary" size="sm" onClick={handleLimpiarFiltros}>Limpiar filtros</Button>}
              </div>
            ) : (
              documentos.map((doc) => <RowCard key={doc._id} doc={doc} />)
            )}
          </div>

          <div className="mt-3">{renderPaginacion()}</div>
        </div>

        <Modal show={mostrarModal} onHide={handleCerrarModal} centered size="xl" fullscreen="lg-down">
          <Modal.Header closeButton>
            <Modal.Title>
              📄 Documento
              {documentoSeleccionado?.includes('rejected_') && <span className="badge bg-danger ms-2">RECHAZADO</span>}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0" style={{ height: '80vh' }}>
            {documentoSeleccionado && (
              <iframe src={documentoSeleccionado} title="Documento PDF" width="100%" height="100%" style={{ border: 'none' }} />
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCerrarModal}>Cerrar</Button>
            {documentoSeleccionado && !documentoSeleccionado.includes('rejected_') && (
              <a href={documentoSeleccionado} download target="_blank" rel="noopener noreferrer" className="btn btn-success">
                <i className="bi bi-download me-1"></i> Descargar
              </a>
            )}
          </Modal.Footer>
        </Modal>

        <Modal show={mostrarModalReenvio} onHide={() => setMostrarModalReenvio(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="bi bi-arrow-repeat me-2"></i>
              Reenviar Documento
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {documentoReenvio && (
              <div>
                <div className="mb-3 p-3 bg-light rounded">
                  <div className="small text-muted mb-1">Documento:</div>
                  <div className="fw-bold">{documentoReenvio.nombreDocumento}</div>
                  <div className="small text-muted mt-2">Paciente:</div>
                  <div>{decodificarNombre(documentoReenvio.nombres)}</div>
                  <div className="small text-muted">CC: {documentoReenvio.cedula}</div>
                </div>

                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <i className="bi bi-envelope me-2"></i>
                      Correo Electrónico
                    </Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="correo@ejemplo.com"
                      value={correoReenvio}
                      onChange={(e) => setCorreoReenvio(e.target.value)}
                      disabled={enviandoReenvio}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      <i className="bi bi-phone me-2"></i>
                      Celular (10 dígitos)
                    </Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder="3001234567"
                      value={celularReenvio}
                      onChange={(e) => setCelularReenvio(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      disabled={enviandoReenvio}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="d-block mb-2">
                      <i className="bi bi-send me-2"></i>
                      Métodos de Envío
                    </Form.Label>
                    
                    <div className="d-flex flex-wrap gap-2">
                      <Form.Check
                        type="checkbox"
                        id="metodo-correo"
                        label={
                          <span>
                            <i className="bi bi-envelope-fill me-1"></i>
                            Correo
                          </span>
                        }
                        checked={metodosReenvio.includes('correo')}
                        onChange={() => handleToggleMetodo('correo')}
                        disabled={enviandoReenvio || !correoReenvio}
                      />
                      
                      <Form.Check
                        type="checkbox"
                        id="metodo-sms"
                        label={
                          <span>
                            <i className="bi bi-chat-dots-fill me-1"></i>
                            SMS
                          </span>
                        }
                        checked={metodosReenvio.includes('sms')}
                        onChange={() => handleToggleMetodo('sms')}
                        disabled={enviandoReenvio || !celularReenvio}
                      />
                    </div>
                    
                    <small className="text-muted d-block mt-2">
                      Los métodos dependen de los datos ingresados arriba
                    </small>
                  </Form.Group>
                </Form>

                {mensajeReenvio && (
                  <Alert 
                    variant={mensajeReenvio.includes('✅') ? 'success' : mensajeReenvio.includes('❌') ? 'danger' : 'info'}
                    className="mb-0"
                  >
                    {mensajeReenvio}
                  </Alert>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setMostrarModalReenvio(false)}
              disabled={enviandoReenvio}
            >
              Cancelar
            </Button>
            <Button 
              variant="primary" 
              onClick={handleReenviarDocumento}
              disabled={enviandoReenvio || (!correoReenvio && !celularReenvio)}
            >
              {enviandoReenvio ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Reenviando...
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-repeat me-2"></i>
                  Reenviar
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
}

export default MedicalDocumentsPage;